import * as React from 'react';
import {useRoute} from 'react-router5';
import {toast} from 'react-toastify';
import {Field, FieldProps, Form, Formik} from 'formik';
import * as Yup from 'yup';
import AuthStore, {SignUpData} from 'stores/AuthStore';
import {Role} from 'stores/UserStore';
import Row from 'components/Row';
import Column from 'components/Column';
import Input from 'components/Input';
import Button, {ButtonVariant} from 'components/Button';
import Link from 'components/Link';
import Tabbar from 'components/Tabbar';
import Tab from 'components/Tab';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import * as helpers from '../helpers';
import styled from "styled-components";

export type SignUpValues
  = SignUpData;

export interface SignInProps {
  initialValues?: SignUpValues;
  renderAfter?: JSX.Element;
}

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-top: 8vh;
  display: flex;
  justify-content: center;
`

/**
 * This is a component of the sign up.
 * @param {SignInProps} props
 * @return {React.ReactElement<SignInProps>}
 * @constructor
 */
export default function SignUp(
    props: SignInProps,
): React.ReactElement<SignInProps> {
  const {router, route} = useRoute();

  const initialValues: SignUpValues = props.initialValues || {
    email: '',
    username: '',
    name: '',
    password: '',
    isTeacher: isTeacher(),
  };

  /**
   * Is the teacher.
   * @return {boolean}
   */
  function isTeacher(): boolean {
    return Boolean(
        route?.params?.role === Role.Teacher,
    );
  }

  /**
   * This function will render after content.
   * @return {JSX.Element | undefined}
   */
  function renderAfterContent(): JSX.Element | undefined {
    return props.renderAfter;
  }

  const validationSchema = Yup.object().shape({
    email: Yup.string().required('Введите электронную почту'),
    username: Yup.string().required('Введите логин'),
    name: Yup.string().required('Введите имя'),
    password: Yup.string().required('Введите пароль'),
  });

  /**
   * This function will render rules.
   * @return {JSX.Element}
   */
  function renderRules(): JSX.Element {
    return (
      <helpers.Text>
        Нажимая “Регистрация”, вы принимаете
        <Link href="/">
          Условия Пользовательского соглашения
        </Link>
        {'и'}
        <Link href="/">
          Политики конфиденциальности
        </Link>.
      </helpers.Text>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <React.Fragment>
        <Row>
          <Column>
            <Field name="username">
              {({field, meta}: FieldProps) => (
                <Input
                  {...field}
                  type="text"
                  label="Логин"
                  error={meta.touched ? (
                    meta.error
                  ) : undefined}
                  placeholder="Логин"
                  required
                />
              )}
            </Field>
          </Column>
        </Row>
        <Row>
          <Column>
            <Field name="email">
              {({field, meta}: FieldProps) => (
                <Input
                  {...field}
                  type="email"
                  label="Электронная почта"
                  error={meta.touched ? (
                    meta.error
                  ) : undefined}
                  placeholder="Электронная почта"
                  required
                />
              )}
            </Field>
          </Column>
        </Row>
        <Row>
          <Column>
            <Field name="name">
              {({field, meta}: FieldProps) => (
                <Input
                  {...field}
                  type="text"
                  label="Имя"
                  error={meta.touched ? (
                    meta.error
                  ) : undefined}
                  placeholder="Имя"
                  required
                />
              )}
            </Field>
          </Column>
        </Row>
        <Row>
          <Column>
            <Field name="password">
              {({field, meta}: FieldProps) => (
                <Input
                  {...field}
                  type="password"
                  label="Пароль"
                  error={meta.touched ? (
                    meta.error
                  ) : undefined}
                  placeholder="Пароль"
                  required
                />
              )}
            </Field>
          </Column>
        </Row>
      </React.Fragment>
    );
  }

  /**
   * This form submit event handler.
   * @param {SignUpValues} values
   */
  function handleSubmit(values: SignUpValues): void {
    AuthStore.signUp({
      ...values,
      isTeacher: isTeacher(),
    }).then((response) => {
      validateResponse(response);
      if (response && !isError(response)) {
        router.navigate('dashboard');
        toast.success('Добро пожаловать!');
      }
    });
  }

  return (
    <Container>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {() => (
          <Form>
            <helpers.Container>
              <helpers.Title>
                Регистрация
              </helpers.Title>
              <helpers.Rows>
                <Tabbar param="role">
                  <Tab name="student" title="Студент">
                    {renderContent()}
                  </Tab>
                  <Tab name="teacher" title="Преподаватель">
                    {renderContent()}
                  </Tab>
                </Tabbar>
                <Row>
                  <Column>
                    <Button
                      type="submit"
                      variant={ButtonVariant.Primary}
                      stretched
                    >
                      Регистрация
                    </Button>
                  </Column>
                </Row>
              </helpers.Rows>
              {renderRules()}
              {renderAfterContent()}
            </helpers.Container>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
